import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { RequireAdmin } from "@/components/shared/require-admin";
import { EmptyState } from "@/components/shared/empty-state";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { DeleteConfirmDialog } from "@/components/shared/delete-confirm-dialog";
import { FormError } from "@/components/shared/form-error";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useUsers,
  useCreateUser,
  useUpdateUser,
  useDeleteUser,
  type User,
  type UserFilters,
} from "@/hooks/use-users";
import { useTeams } from "@/hooks/use-teams";
import { compareUsersByLastName, formatNameLastFirst } from "@/lib/format-name";

const userFormSchema = z.object({
  name: z.string().min(1, "Name is required").max(255),
  email: z.string().email("Invalid email address"),
  role: z.enum(["employee", "supervisor", "admin"]),
  team_id: z.string().uuid("Select a team"),
  manager_id: z.string().uuid().nullable(),
});

type UserFormValues = z.infer<typeof userFormSchema>;

const ROLE_LABELS: Record<string, string> = {
  employee: "Employee",
  supervisor: "Supervisor",
  admin: "Admin",
};

const ROLE_VARIANTS: Record<string, "default" | "secondary" | "outline"> = {
  employee: "secondary",
  supervisor: "default",
  admin: "outline",
};

function UsersTableSkeleton() {
  return (
    <div className="space-y-3" role="status" aria-label="Loading users">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead className="hidden sm:table-cell">Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead className="hidden md:table-cell">Team</TableHead>
              <TableHead className="w-24">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                <TableCell className="hidden sm:table-cell"><Skeleton className="h-4 w-48" /></TableCell>
                <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                <TableCell className="hidden md:table-cell"><Skeleton className="h-4 w-28" /></TableCell>
                <TableCell><Skeleton className="h-8 w-20" /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function UserFormFields({
  register,
  errors,
  setValue,
  watch,
  teams,
  allUsers,
  editingUserId,
}: {
  register: ReturnType<typeof useForm<UserFormValues>>["register"];
  errors: ReturnType<typeof useForm<UserFormValues>>["formState"]["errors"];
  setValue: ReturnType<typeof useForm<UserFormValues>>["setValue"];
  watch: ReturnType<typeof useForm<UserFormValues>>["watch"];
  teams: Array<{ id: string; name: string }>;
  allUsers: User[];
  editingUserId: string | null;
}) {
  return (
    <>
      <div className="space-y-2">
        <Label htmlFor="user-name">Name</Label>
        <Input id="user-name" placeholder="e.g., John Smith" {...register("name")} />
        <FormError message={errors.name?.message} />
      </div>

      <div className="space-y-2">
        <Label htmlFor="user-email">Email</Label>
        <Input id="user-email" type="email" placeholder="e.g., jsmith@hillsdale.edu" {...register("email")} />
        <FormError message={errors.email?.message} />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label>Role</Label>
          <Select value={watch("role")} onValueChange={(v) => setValue("role", v as "employee" | "supervisor" | "admin")}>
            <SelectTrigger aria-label="User role"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="employee">Employee</SelectItem>
              <SelectItem value="supervisor">Supervisor</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Team</Label>
          <Select value={watch("team_id")} onValueChange={(v) => setValue("team_id", v)}>
            <SelectTrigger aria-label="User team"><SelectValue placeholder="Select team" /></SelectTrigger>
            <SelectContent>
              {teams.map((team) => (
                <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FormError message={errors.team_id?.message} />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Manager</Label>
        <Select value={watch("manager_id") ?? "none"} onValueChange={(v) => setValue("manager_id", v === "none" ? null : v)}>
          <SelectTrigger aria-label="User manager"><SelectValue placeholder="No manager" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No Manager</SelectItem>
            {[...allUsers]
              .filter((u) => u.id !== editingUserId)
              .sort(compareUsersByLastName)
              .map((u) => (
                <SelectItem key={u.id} value={u.id}>{formatNameLastFirst(u.name)}</SelectItem>
              ))}
          </SelectContent>
        </Select>
      </div>
    </>
  );
}

function getUserFormDefaults(user: User | null, fallbackTeamId: string): UserFormValues {
  return {
    name: user?.name ?? "",
    email: user?.email ?? "",
    role: user?.role ?? ("employee" as const),
    team_id: user?.team_id ?? fallbackTeamId,
    manager_id: user?.manager_id ?? null,
  };
}

function getSubmitLabel(isEditing: boolean, isPending: boolean): string {
  if (isPending) return isEditing ? "Saving..." : "Creating...";
  return isEditing ? "Save" : "Create";
}

function UserFormDialog({
  open,
  onOpenChange,
  user,
  teams,
  allUsers,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User | null;
  teams: Array<{ id: string; name: string }>;
  allUsers: User[];
}) {
  const isEditing = user !== null;
  const createUser = useCreateUser();
  const updateUser = useUpdateUser(user?.id ?? "");

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<UserFormValues>({
    resolver: zodResolver(userFormSchema),
    defaultValues: getUserFormDefaults(user, teams[0]?.id ?? ""),
  });

  useEffect(() => {
    reset(getUserFormDefaults(user, teams[0]?.id ?? ""));
  }, [user, teams, reset]);

  const isPending = createUser.isPending || updateUser.isPending;

  const closeAndReset = () => {
    onOpenChange(false);
    reset();
  };

  const onSubmit = (data: UserFormValues) => {
    if (isEditing) {
      updateUser.mutate(data, { onSuccess: closeAndReset });
    } else {
      createUser.mutate({ ...data, department_head: false }, { onSuccess: closeAndReset });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit User" : "Create User"}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Update user details, role, and team assignment."
              : "Add a new user to the system."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <UserFormFields
            register={register}
            errors={errors}
            setValue={setValue}
            watch={watch}
            teams={teams}
            allUsers={allUsers}
            editingUserId={user?.id ?? null}
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {getSubmitLabel(isEditing, isPending)}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// DeleteUserDialog replaced by DeleteConfirmDialog (inline at usage site)

export function AdminUsersPage() {
  const [filters, setFilters] = useState<UserFilters>({});
  const [formOpen, setFormOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const deleteUser = useDeleteUser();
  const [accumulatedUsers, setAccumulatedUsers] = useState<User[]>([]);
  const prevCursorRef = useRef<string | undefined>(undefined);

  const { data, isLoading, error } = useUsers(filters);
  const { data: teamsData } = useTeams();
  const pageUsers = data?.data ?? [];
  const teams = teamsData?.data ?? [];
  const nextCursor = data?.next_cursor ?? null;

  useEffect(() => {
    if (pageUsers.length === 0) return;
    if (filters.cursor && filters.cursor !== prevCursorRef.current) {
      // Appending a new page
      setAccumulatedUsers((prev) => [...prev, ...pageUsers]);
    } else if (!filters.cursor) {
      // First page or filters changed
      setAccumulatedUsers(pageUsers);
    }
    prevCursorRef.current = filters.cursor;
  }, [pageUsers, filters.cursor]);

  const users = accumulatedUsers;

  const teamMap = new Map(teams.map((t) => [t.id, t.name]));

  const handleEdit = (user: User) => {
    setSelectedUser(user);
    setFormOpen(true);
  };

  const handleDeleteClick = (user: User) => {
    setSelectedUser(user);
    setDeleteOpen(true);
  };

  const handleCreate = () => {
    setSelectedUser(null);
    setFormOpen(true);
  };

  const handleRoleFilter = (value: string) => {
    const role = value === "all" ? undefined : (value as UserFilters["role"]);
    setFilters((prev) => ({ ...prev, role, cursor: undefined }));
  };

  const handleTeamFilter = (value: string) => {
    const teamId = value === "all" ? undefined : value;
    setFilters((prev) => ({ ...prev, team_id: teamId, cursor: undefined }));
  };

  const loadMore = () => {
    if (nextCursor) {
      setFilters((prev) => ({ ...prev, cursor: nextCursor }));
    }
  };

  return (
    <RequireAdmin>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="font-heading text-2xl font-semibold sm:text-3xl">User Management</h1>
          <Button onClick={handleCreate} aria-label="Create new user">
            <Plus className="mr-2 h-4 w-4" />
            New User
          </Button>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row">
          <Select value={filters.role ?? "all"} onValueChange={handleRoleFilter}>
            <SelectTrigger className="w-full sm:w-40" aria-label="Filter by role">
              <SelectValue placeholder="All Roles" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="employee">Employee</SelectItem>
              <SelectItem value="supervisor">Supervisor</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filters.team_id ?? "all"} onValueChange={handleTeamFilter}>
            <SelectTrigger className="w-full sm:w-48" aria-label="Filter by team">
              <SelectValue placeholder="All Teams" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Teams</SelectItem>
              {teams.map((team) => (
                <SelectItem key={team.id} value={team.id}>
                  {team.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {isLoading ? (
          <UsersTableSkeleton />
        ) : error ? (
          <EmptyState
            title="Failed to load users"
            description={error.message}
          />
        ) : users.length === 0 ? (
          <EmptyState
            title="No users found"
            description="Create users or adjust your filters."
            actionLabel="New User"
            onAction={handleCreate}
          />
        ) : (
          <>
            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead className="hidden sm:table-cell">Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead className="hidden md:table-cell">Team</TableHead>
                    <TableHead className="w-24">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">
                        <div>
                          {user.name}
                          <span className="block text-xs text-[hsl(var(--muted-foreground))] sm:hidden">
                            {user.email}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">{user.email}</TableCell>
                      <TableCell>
                        <Badge variant={ROLE_VARIANTS[user.role] ?? "secondary"}>
                          {ROLE_LABELS[user.role] ?? user.role}
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {teamMap.get(user.team_id) ?? "Unknown"}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(user)}
                            aria-label={`Edit ${user.name}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteClick(user)}
                            aria-label={`Delete ${user.name}`}
                          >
                            <Trash2 className="h-4 w-4 text-[hsl(var(--destructive))]" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {nextCursor && (
              <div className="flex justify-center">
                <Button variant="outline" onClick={loadMore}>
                  Load More
                </Button>
              </div>
            )}
          </>
        )}

        <UserFormDialog
          open={formOpen}
          onOpenChange={setFormOpen}
          user={selectedUser}
          teams={teams}
          allUsers={users}
        />

        <DeleteConfirmDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          title="Delete User"
          description={<>Are you sure you want to delete <strong>{selectedUser?.name}</strong>? This action cannot be undone.</>}
          onConfirm={() => { if (selectedUser) deleteUser.mutate(selectedUser.id, { onSuccess: () => setDeleteOpen(false) }); }}
          isPending={deleteUser.isPending}
        />
      </div>
    </RequireAdmin>
  );
}
